import type { ReactNode } from "react";
import { CloseIcon } from "../icons/CloseIcon";
import { ModalSheet } from "./ModalSheet";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
type IconTone = "accent" | "muted" | "danger";

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  /** Icon tile shown to the left of the title (38x38). */
  /**
   * Raise this dialog above the default modal layer (backdrop + sheet) when
   * it opens on top of another modal. Forwarded to ModalSheet.
   */
  elevated?: boolean;
  /** Small icon tile shown to the left of the title (32x32). */
  icon?: ReactNode;
  /** Tone for the icon tile — "accent" (solid accent fill) or "muted" (soft surface). */
  iconTone?: IconTone;
  /** Required dialog title — bold 15px. */
  title?: ReactNode;
  /** Subtitle line below title — 12px muted. */
  subtitle?: ReactNode;
  /** Accessible label for the close (X) button. */
  closeLabel?: string;
  /** Slot rendered before the icon/title (e.g. a back button on multi-view sheets). */
  headerLeading?: ReactNode;
  /** Slot rendered between the title block and the close X (e.g. a "+ Add" CTA). */
  headerTrailing?: ReactNode;
  /** When provided, replaces the icon + title + subtitle stack entirely (e.g. brand logo). */
  headerOverride?: ReactNode;
  /** Status / hint slot on the left side of the footer (12px muted). */
  footerLeft?: ReactNode;
  /** Action buttons on the right side of the footer. */
  footerActions?: ReactNode;
  /** Extra classes for the scrollable body wrapper (e.g. a tinted background or custom padding). */
  bodyClassName?: string;
  /** Extra classes for the footer element (e.g. a matching background). */
  footerClassName?: string;
  /**
   * Mobile-only header rendered above the default header.
   * When provided: shown only on mobile (md:hidden), and the default
   * header becomes desktop-only (hidden md:flex).
   * When NOT provided: default header renders at all breakpoints (unchanged).
   */
  mobileHeader?: ReactNode;
  /**
   * Presentation. "sheet" (default) = bottom sheet on mobile, centered modal
   * on desktop. "drawer" = bottom sheet on mobile, full-height end-anchored
   * slide-in panel on desktop.
   */
  variant?: "sheet" | "drawer";
  /** Hide the footer (use when actions live inline in the body). */
  hideFooter?: boolean;
  /**
   * Conditionally render the footer. Pass `false` for sticky-when-dirty
   * patterns: `<Modal showFooterWhen={isDirty} ... />`.
   */
  showFooterWhen?: boolean;
  children: ReactNode;
};

/**
 * Reusable dialog with consistent chrome: icon + title + subtitle in the
 * header, scrollable body, and a left-status / right-actions footer. Built
 * on top of `ModalSheet` so it inherits the responsive sheet/modal behavior,
 * focus trap, scroll lock, escape-to-close, and entrance animation.
 */
export function Modal({
  isOpen,
  onClose,
  size = "lg",
  elevated = false,
  icon,
  iconTone = "accent",
  title,
  subtitle,
  closeLabel = "Close",
  headerLeading,
  headerTrailing,
  headerOverride,
  mobileHeader,
  footerLeft,
  footerActions,
  bodyClassName,
  footerClassName,
  variant = "sheet",
  hideFooter = false,
  showFooterWhen,
  children,
}: ModalProps) {
  const footerVisible =
    !hideFooter && (showFooterWhen === undefined || showFooterWhen);

  const iconTileBase =
    "inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px]";
  const iconTileClass =
    iconTone === "danger"
      ? `${iconTileBase} bg-[color:color-mix(in_oklab,var(--danger-500,var(--error-500))_13%,transparent)] text-[color:var(--danger-500,var(--error-500))]`
      : iconTone === "accent"
        ? `${iconTileBase} bg-[color:var(--accent-500)] text-[color:var(--on-accent,#fff)]`
        : `${iconTileBase} bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]`;

  return (
    <ModalSheet
      isOpen={isOpen}
      size={size}
      onClose={onClose}
      elevated={elevated}
      variant={variant}
    >
      {mobileHeader ? <div className="md:hidden">{mobileHeader}</div> : null}
      <header
        className={[
          "flex shrink-0 items-start gap-3 px-4 pb-3 pt-[15px]",
          mobileHeader ? "hidden md:flex" : "",
        ]
          .join(" ")
          .trim()}
      >
        {headerLeading ? (
          <span className="shrink-0">{headerLeading}</span>
        ) : null}
        {headerOverride ? (
          <div className="flex min-w-0 flex-1 items-center">
            {headerOverride}
          </div>
        ) : (
          <>
            {icon ? <span className={iconTileClass}>{icon}</span> : null}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
              {title ? (
                <span className="truncate text-[16px] font-bold text-[color:var(--text-primary)]">
                  {title}
                </span>
              ) : null}
              {subtitle ? (
                <span className="truncate text-[12.5px] text-[color:var(--text-muted)]">
                  {subtitle}
                </span>
              ) : null}
            </div>
          </>
        )}
        {headerTrailing ? (
          <span className="shrink-0">{headerTrailing}</span>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))] hover:text-[color:var(--text-primary)]"
        >
          <CloseIcon size={16} />
        </button>
      </header>
      {/* Body content floats as a white inset card on the gray panel. */}
      <div
        className={[
          "flex min-h-0 flex-1 px-3",
          footerVisible ? "" : "pb-3",
        ].join(" ")}
      >
        <div
          className={[
            "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-[14px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] p-[18px]",
            bodyClassName ?? "",
          ].join(" ")}
        >
          {children}
        </div>
      </div>
      {footerVisible ? (
        <footer
          className={[
            "flex shrink-0 items-center gap-3 px-[18px] pb-4 pt-[13px]",
            footerClassName ?? "",
          ].join(" ")}
        >
          {footerLeft ? (
            <span className="min-w-0 text-[12px] text-[color:var(--text-muted)]">
              {footerLeft}
            </span>
          ) : null}
          <div className="ms-auto flex items-center gap-2.5">
            {footerActions}
          </div>
        </footer>
      ) : null}
    </ModalSheet>
  );
}
